import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import useBranchesFetcher from '../hooks/useBranchesFetcher'
import useBranchPoster from '../hooks/useBranchPoster'
import { validateFields, Rule } from '../utils/validateForm'
import Branch from '../interfaces/Branch'
import { useAuth } from '../hooks/useAuth'
import { AppPath } from '../enums/AppPath'

import { ButtonAction } from './Buttons'
import FoldableForm from './FoldableForm' // Import the new component

const BranchEdit: React.FC = () => {
  const { fetchBranchDetail } = useBranchesFetcher()
  const { errorPostBranch, postBranch } = useBranchPoster()
  const { userData } = useAuth()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const isCreating = !id

  const [branchData, setBranchData] = useState<Branch>({
    _id: '',
    name: '',
    createdAt: '',
    updatedAt: '',
    __v: 0,
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    streetNumber: 0,
    addressComponents: '',
    organization: userData?.organization?._id ?? '',
    municipality: '',
  })

  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [localError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDataAsync = async () => {
      if (isCreating || !id) return
      const branchFull = await fetchBranchDetail(id)

      if (branchFull) {
        setBranchData(branchFull)
      }
    }
    fetchDataAsync()
  }, [id, isCreating])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setBranchData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSaveClick = async () => {
    const validationErrors = validateFields([
      { name: 'name', value: branchData.name, rule: Rule.REQUIRED },
      { name: 'email', value: branchData.email, rule: Rule.REQUIRED },
    ])

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return
    }

    const response = await postBranch(branchData, id || '')

    if (!response) return

    navigate(isCreating ? `/${AppPath.Branches}` : `/${AppPath.Branches}/view/${id}`)
  }

  // Define the structure for the FoldableForm
  const formSections = [
    {
      id: 'generalInfo',
      title: 'Información General',
      fields: [{ name: 'name', label: 'Nombre', type: 'text' as const, validation: Rule.REQUIRED }],
    },
    {
      id: 'contactInfo',
      title: 'Datos de Contacto',
      fields: [
        { name: 'email', label: 'Email', type: 'email' as const, validation: Rule.REQUIRED },
        { name: 'phone', label: 'Teléfono', type: 'text' as const },
        { name: 'whatsapp', label: 'Whatsapp', type: 'text' as const },
      ],
    },
    {
      id: 'locationInfo',
      title: 'Información de Ubicación',
      fields: [
        { name: 'address', label: 'Dirección (Calle, Comuna, Ciudad)', type: 'text' as const },
        { name: 'streetNumber', label: 'Número', type: 'number' as const },
        { name: 'addressComponents', label: 'Casa, depto, u oficina', type: 'text' as const },
      ],
    },
  ]

  return (
    <div className="max-w-4xl p-6 bg-white shadow-md rounded-lg space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">
        {isCreating ? 'Crear Nueva Sucursal' : 'Actualizar Sucursal'}
      </h2>

      {errorPostBranch && (
        <div className="text-red-500">
          La sucursal con el nombre '{branchData.name}' ya está registrada en el sistema.
        </div>
      )}
      {localError && <div className="text-red-500">{localError}</div>}

      <FoldableForm
        sections={formSections}
        formData={branchData}
        formErrors={errors}
        onInputChange={handleInputChange}
      />

      <div className="pt-4">
        <ButtonAction label={isCreating ? 'Crear' : 'Actualizar'} handleButtonClick={handleSaveClick} />
      </div>
    </div>
  )
}

export default BranchEdit
